export type SortDir = "asc" | "desc";
export interface DashboardSort {
  id: string;
  dir: SortDir;
}
export type DashboardView = "table" | "list" | "grid";

export interface DashboardQueryState {
  search: string;
  filters: Record<string, string[]>;
  sort: DashboardSort | null;
  page: number;
  pageSize: number;
  view: DashboardView | null;
}

export const DEFAULT_PAGE_SIZE = 25;

export const DEFAULT_QUERY_STATE: DashboardQueryState = {
  search: "",
  filters: {},
  sort: null,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  view: null,
};

const VIEWS: DashboardView[] = ["table", "list", "grid"];

// URL-key helpers. A `namespace` lets multiple collections share one URL
// without clobbering each other: keys become `<ns>.q`, `<ns>.f.<field>`, …
const key = (name: string, ns: string) => (ns ? `${ns}.${name}` : name);
const filterPrefix = (ns: string) => (ns ? `${ns}.f.` : "f.");

function belongsToNamespace(k: string, ns: string): boolean {
  return (
    k === key("q", ns) ||
    k === key("sort", ns) ||
    k === key("page", ns) ||
    k === key("size", ns) ||
    k === key("view", ns) ||
    k.startsWith(filterPrefix(ns))
  );
}

/** Return a copy of `params` with this namespace's keys removed (others kept). */
export function clearNamespace(
  params: URLSearchParams,
  namespace = "",
): URLSearchParams {
  const out = new URLSearchParams();
  for (const [k, v] of params.entries()) {
    if (!belongsToNamespace(k, namespace)) out.append(k, v);
  }
  return out;
}

export function parseQueryState(
  params: URLSearchParams,
  namespace = "",
): DashboardQueryState {
  const fp = filterPrefix(namespace);
  const filters: Record<string, string[]> = {};
  for (const [k, value] of params.entries()) {
    if (!k.startsWith(fp)) continue;
    const name = k.slice(fp.length);
    if (!name) continue;
    filters[name] = value.split(",").filter(Boolean);
  }

  let sort: DashboardSort | null = null;
  const sortRaw = params.get(key("sort", namespace));
  if (sortRaw) {
    const [id, dir] = sortRaw.split(".");
    if (id && (dir === "asc" || dir === "desc")) sort = { id, dir };
  }

  const viewRaw = params.get(key("view", namespace));
  const view = VIEWS.includes(viewRaw as DashboardView)
    ? (viewRaw as DashboardView)
    : null;

  const page = Number.parseInt(params.get(key("page", namespace)) ?? "", 10);
  const size = Number.parseInt(params.get(key("size", namespace)) ?? "", 10);

  return {
    search: params.get(key("q", namespace)) ?? "",
    filters,
    sort,
    page: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize: Number.isFinite(size) && size > 0 ? size : DEFAULT_PAGE_SIZE,
    view,
  };
}

export function serializeQueryState(
  state: DashboardQueryState,
  namespace = "",
): URLSearchParams {
  const params = new URLSearchParams();
  if (state.search) params.set(key("q", namespace), state.search);
  for (const [name, values] of Object.entries(state.filters)) {
    if (values.length) {
      params.set(`${filterPrefix(namespace)}${name}`, values.join(","));
    }
  }
  if (state.sort) {
    params.set(key("sort", namespace), `${state.sort.id}.${state.sort.dir}`);
  }
  if (state.page > 1) params.set(key("page", namespace), String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set(key("size", namespace), String(state.pageSize));
  }
  if (state.view) params.set(key("view", namespace), state.view);
  return params;
}
